import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Calendar, Instagram, Facebook } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Reveal, Overline } from "@/components/Reveal";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Logo } from "@/components/Logo";

const TikTokIcon = ({ size = 18, ...props }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" {...props}>
    <path d="M16.6 5.82c-1-.93-1.5-2.16-1.5-3.62h-3.1v13.87c0 1.62-1.32 2.94-2.94 2.94a2.94 2.94 0 0 1 0-5.88c.3 0 .59.05.86.13V9.99a6.05 6.05 0 0 0-.86-.06 6.05 6.05 0 1 0 6.05 6.05V8.62c1.15.82 2.55 1.3 4.06 1.3V6.83c-.9 0-1.79-.36-2.57-1.01Z" />
  </svg>
);

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });

export const ArticlesPage = () => {
  const { settings } = useSettings();
  const { data, isLoading } = useQuery({
    queryKey: ["articles-public"],
    queryFn: async () => (await api.get("/articles", { params: { published_only: true } })).data,
    retry: 1,
  });
  const articles = Array.isArray(data) ? data : [];

  const socials = [
    { url: settings.instagram_url, label: "Instagram", Icon: Instagram },
    { url: settings.facebook_url, label: "Facebook", Icon: Facebook },
    { url: settings.tiktok_url, label: "TikTok", Icon: TikTokIcon },
  ].filter((s) => s.url);

  return (
    <div className="bg-cream min-h-screen">
      <header className="border-b border-border/60">
        <div className="px-6 md:px-10 py-5 flex items-center justify-between max-w-7xl mx-auto">
          <Link to="/" className="font-brand font-bold text-xl tracking-tight text-ink flex items-center gap-2">
            <Logo src={settings.logo_url} size={28} />
            {settings.brand_name}
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-stone2 hover:text-honey-dark transition-colors duration-300"
          >
            <ArrowLeft size={16} />
            Kembali ke Beranda
          </Link>
        </div>
      </header>

      <section className="px-6 md:px-10 py-16 md:py-24 max-w-7xl mx-auto">
        <Reveal>
          <Overline>Artikel</Overline>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-ink mb-4">
            Cerita dan tips seputar madu.
          </h1>
          <p className="text-stone2 max-w-xl mb-16">
            Baca kisah di balik produksi madu kami serta tips memilih dan
            mengonsumsi madu murni.
          </p>
        </Reveal>

        {isLoading && <p className="text-stone2">Memuat artikel...</p>}
        {!isLoading && articles.length === 0 && (
          <p className="text-stone2">Belum ada artikel yang dipublikasikan.</p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Article list */}
          <div className="lg:col-span-8">
            {articles.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.06} y={0}>
                <Link
                  to={`/artikel/${a.slug}`}
                  data-testid={`article-card-${a.slug}`}
                  className="group flex flex-col sm:flex-row gap-6 py-8 border-b border-border/60 first:pt-0"
                >
                  <div className="sm:w-64 shrink-0 overflow-hidden rounded-2xl bg-cream-200 aspect-[4/3]">
                    {a.cover_image ? (
                      <img
                        src={a.cover_image}
                        alt={a.title}
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    ) : (
                      <div className="h-full w-full bg-cream-200" />
                    )}
                  </div>
                  <div className="flex-1 flex flex-col">
                    <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-honey-dark mb-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-honey" />
                      Artikel
                    </span>
                    <h2 className="font-display text-2xl md:text-3xl tracking-tight text-ink mb-2 group-hover:text-brandRed transition-colors duration-300">
                      {a.title}
                    </h2>
                    <p className="text-stone2 leading-relaxed text-[15px] flex-1">{a.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between gap-4">
                      <span className="inline-flex items-center gap-1.5 text-xs text-stone2/70">
                        <Calendar size={14} />
                        {formatDate(a.created_at)}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brandRed">
                        Baca
                        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-10 lg:sticky lg:top-24">
            <Reveal y={0}>
              <div className="rounded-3xl bg-cream-200 p-8 text-center">
                <div className="mx-auto mb-5 h-28 w-28 overflow-hidden rounded-full border-4 border-cream shadow-md">
                  <img
                    src={settings.about_image}
                    alt={settings.brand_name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <h3 className="font-display text-2xl text-ink mb-1">{settings.brand_name}</h3>
                <p className="text-xs font-bold uppercase tracking-widest text-honey-dark mb-4">
                  {settings.location_text}
                </p>
                <p className="text-stone2 text-sm leading-relaxed">{settings.about_intro}</p>
                <div className="font-display italic text-3xl text-honey-dark/70 mt-5 mb-1">
                  {settings.brand_name}
                </div>
                {socials.length > 0 && (
                  <div className="mt-5 flex justify-center gap-3">
                    {socials.map(({ url, label, Icon }) => (
                      <a
                        key={label}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={label}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-border/60 text-stone2 hover:bg-honey hover:text-ink hover:border-honey transition-colors duration-300"
                      >
                        <Icon size={18} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>

            {articles.length > 0 && (
              <Reveal delay={0.1} y={0}>
                <h3 className="font-display text-2xl text-ink mb-5">Artikel Lainnya</h3>
                <div className="space-y-4">
                  {articles.slice(0, 4).map((a) => (
                    <Link
                      key={a.id}
                      to={`/artikel/${a.slug}`}
                      className="group flex items-center gap-4 rounded-2xl bg-cream-200 p-3 hover:bg-honey-light/60 transition-colors duration-300"
                    >
                      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-cream">
                        {a.cover_image ? (
                          <img src={a.cover_image} alt={a.title} className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full bg-cream" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <span className="inline-block mb-1 rounded-full bg-honey-light px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-honey-dark">
                          Artikel
                        </span>
                        <h4 className="text-sm font-semibold text-ink leading-snug line-clamp-2 group-hover:text-brandRed transition-colors duration-300">
                          {a.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </Reveal>
            )}
          </aside>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};
